import { Box, Typography } from '@mui/material';
import { JsonView, darkStyles } from 'react-json-view-lite';
import { JSONInputTypes, JsonLabel } from './DebugInfoSection';

type JsonItemProps = {
  item: JSONInputTypes;
  jsonLabels?: JsonLabel[] | string | undefined;
};

/**
 * Component that wraps a single JSON object in a <JsonView /> component.
 *
 * @prop item - Single JSON object to be displayed
 * @prop label - Optional label to be displayed in the top right corner of the JSON view
 */
export const JsonItem = ({ item, jsonLabels }: JsonItemProps) => {
  if (item === undefined || item === null) {
    return null;
  }

  // Check if there is a label for the item in the jsonLabels array by first checking if the item has an id property
  // and then checking if the id exists in the jsonLabels array. If it does, set the label to the corresponding label from jsonLabels.
  let label: string | undefined = undefined;
  if (jsonLabels) {
    // If label is a string, we assume it's a single label for the item
    if (typeof jsonLabels === 'string') {
      label = jsonLabels;
    } else {
      // Check if the item has an id property and use it to find the corresponding label in jsonLabels
      let itemId = (item as { id?: string }).id;
      // scan data is an exception, where the id is actually detection_id, so we need to check for that as well
      if (!itemId) {
        itemId = (item as { detection_id?: string }).detection_id;
      }

      // Find the label in jsonLabels that matches the itemId
      label = jsonLabels?.find((jsonLabel) => jsonLabel.id === itemId)?.label;
    }
  }

  return (
    <Box
      sx={{
        width: '100%',
        mb: '0.5rem',
        mt: '0.2rem',
        position: 'relative',
      }}
    >
      <JsonView
        data={item}
        shouldInitiallyExpand={() => false}
        style={darkStyles}
      />
      {label && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 10,
            color: (theme) => theme.colors.common.bloomfieldGreen,
          }}
        >
          <Typography>{label}</Typography>
        </Box>
      )}
    </Box>
  );
};
